import { Chip, FlexContainer, Icon, Text } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import { locizeNS } from 'Services/i18n';
import useTranslation from 'Hooks/useTranslation';
import { PreviousYearsResultType, PreviousYearStatus } from 'Containers/PreviousYears/types';
import {
  getAmountToRecoverage,
  getTextColor,
  isReadyAndNotZero,
  isTaxYear2019Recoverable,
} from 'Containers/Dashboard/components/PreviousYearBanner/utils';
import * as Styled from './styles';

const tContext = {
  [PreviousYearsResultType.ZERO]: 'zero',
  [PreviousYearsResultType.TO_RETURN]: 'toReturn',
  [PreviousYearsResultType.TO_PAY]: 'toPay',
};

const PreviousYearContent = ({
  data,
  needEfirma,
}: {
  data: PueblaPreviousYearsResponse;
  needEfirma: boolean;
}): JSX.Element => {
  const { t } = useTranslation(locizeNS.PY);
  const userIsReadyAndNotZero = isReadyAndNotZero(data);
  const taxYear2019Recoverable = isTaxYear2019Recoverable(data);
  const isSubmitted = data?.stage === PreviousYearStatus.SUBMITTED;
  const isInProgress = isSubmitted && !needEfirma;
  const amountToRecoverage = getAmountToRecoverage(data);

  const renderUserPrice = userIsReadyAndNotZero || amountToRecoverage;
  const textColor = getTextColor(taxYear2019Recoverable, needEfirma, isInProgress);

  return (
    <FlexContainer direction="column" gap="dimension_08">
      {isSubmitted && (
        <Chip
          size="S"
          type={needEfirma ? 'warning_shaded' : isSubmitted ? 'success_shaded' : 'neutral_line'}
        >
          {t('PreviousYear.result.calculating', {
            context: (needEfirma && 'efirma') || (isSubmitted && 'submitted'),
          })}
        </Chip>
      )}
      <Styled.TitleContainers direction="column" gap="dimension_08">
        <Text type="headline" size="S_L_S" color="dark">
          {t('PreviousYearBanner.title')}
        </Text>
        <Text type="paragraphs" size="S_L_L" color="dark">
          {t('PreviousYearBanner.mobile.title')}
        </Text>
      </Styled.TitleContainers>
      {taxYear2019Recoverable && !needEfirma && (
        <FlexContainer direction="column" gap="dimension_08">
          <FlexContainer direction="row" gap="dimension_08" align="center">
            <Icon icon="WarningRoundedSolidXS" color="error" />
            <Text type="paragraphs" size="M_L_S" color="danger_strong">
              {t('PreviousYear.year.lastCalculation')}
            </Text>
          </FlexContainer>
          <Text type="paragraphs" size="XS_L_S" color="danger_strong">
            {t('PreviousYear.year.lastCalculation.description')}
          </Text>
        </FlexContainer>
      )}
      {isReadyAndNotZero(data) && !taxYear2019Recoverable && (
        <Text type="paragraphs" size="M_L_S" color="dark">
          {t('PreviousYear.result.title', {
            context: tContext[data.refundStatusType],
          })}
        </Text>
      )}
      {Boolean(renderUserPrice) && (
        <Text type="paragraphs" size="M_L_S" color={textColor}>
          {t('PreviousYear.result.amount', {
            context: needEfirma && 'efirma',
            amount: YayoyakiFormatter.formatAmountMxn(
              Math.abs(data?.amountPending) || amountToRecoverage
            ),
          })}
        </Text>
      )}
      <Text type="paragraphs" size="XS_M_M" color="grey">
        {t('PreviousYear.result.description', {
          context:
            isSubmitted && !needEfirma
              ? 'submitted'
              : needEfirma
                ? 'efirma'
                : tContext[data.refundStatusType],
        })}
      </Text>
    </FlexContainer>
  );
};

export default PreviousYearContent;
